import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Download, X, Smartphone } from 'lucide-react';
import { useIsMobile } from '@/hooks/use-mobile';
import { Z_INDEX } from '@/lib/zIndex';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

export const PWAInstallPrompt = () => {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [showPrompt, setShowPrompt] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);
  const isMobile = useIsMobile();

  useEffect(() => {
    // Vérifier si déjà installé (mode standalone)
    const isInStandaloneMode = window.matchMedia('(display-mode: standalone)').matches ||
      (window.navigator as any).standalone === true;
    
    setIsStandalone(isInStandaloneMode);

    // Détecter iOS
    const iOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !(window as any).MSStream;
    setIsIOS(iOS);

    // Vérifier si l'utilisateur a déjà refusé
    const hasDeclined = localStorage.getItem('pwa-install-declined');
    const declinedAt = hasDeclined ? parseInt(hasDeclined) : 0;
    const daysSinceDeclined = (Date.now() - declinedAt) / (1000 * 60 * 60 * 24);

    // Ne pas afficher si déjà installé ou refusé récemment (< 7 jours)
    if (isInStandaloneMode || (hasDeclined && daysSinceDeclined < 7)) {
      return;
    }

    // Vérifier si l'utilisateur a déjà répondu aux cookies
    const checkCookieConsent = () => {
      const hasAnsweredCookies = localStorage.getItem('cookie-consents-answered');
      return hasAnsweredCookies === 'true';
    };

    // Écouter l'événement beforeinstallprompt (Android/Desktop)
    const handleBeforeInstall = (e: Event) => {
      e.preventDefault();
      const installEvent = e as BeforeInstallPromptEvent;
      setDeferredPrompt(installEvent);
      
      // Attendre que l'utilisateur ait répondu aux cookies avant d'afficher le prompt PWA
      const checkInterval = setInterval(() => {
        if (checkCookieConsent()) {
          clearInterval(checkInterval);
          setTimeout(() => {
            setShowPrompt(true);
          }, 2000);
        }
      }, 500);

      // Timeout de sécurité après 30 secondes
      setTimeout(() => {
        clearInterval(checkInterval);
        if (checkCookieConsent()) {
          setShowPrompt(true);
        }
      }, 30000);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstall);

    // Pour iOS, afficher le prompt manuellement après les cookies
    if (iOS && !isInStandaloneMode) {
      const checkInterval = setInterval(() => {
        if (checkCookieConsent()) {
          clearInterval(checkInterval);
          setTimeout(() => {
            setShowPrompt(true);
          }, 2000);
        }
      }, 500);

      setTimeout(() => {
        clearInterval(checkInterval);
        if (checkCookieConsent()) {
          setShowPrompt(true);
        }
      }, 30000);
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
    };
  }, []);

  const handleInstallClick = async () => {
    if (deferredPrompt) {
      // Android/Desktop - utiliser le prompt natif
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      
      if (outcome === 'accepted') {
        console.log('✅ PWA installée');
      }
      
      setDeferredPrompt(null);
      setShowPrompt(false);
    }
    // Pour iOS, le prompt reste affiché avec les instructions
  };

  const handleDismiss = () => {
    localStorage.setItem('pwa-install-declined', Date.now().toString());
    setShowPrompt(false);
  };

  // Ne rien afficher si déjà installé ou pas de prompt
  if (isStandalone || !showPrompt) {
    return null;
  }

  // Version iOS (instructions manuelles)
  if (isIOS) {
    return (
      <Card 
        className="fixed bottom-20 md:bottom-4 left-4 right-4 md:left-auto md:right-4 md:max-w-md p-4 shadow-lg border-2 animate-slide-up"
        style={{ zIndex: Z_INDEX.toast }}
      >
        <button
          onClick={handleDismiss}
          className="absolute top-2 right-2 p-1 hover:bg-muted rounded-full transition-colors"
          aria-label="Fermer"
        >
          <X className="h-4 w-4" />
        </button>

        <div className="flex items-start gap-3 pr-6">
          <div className="bg-primary/10 p-2 rounded-full flex-shrink-0">
            <Smartphone className="h-5 w-5 text-primary" />
          </div>
          <div className="flex-1 space-y-2">
            <h3 className="font-semibold">Installer First Tuesday</h3>
            <p className="text-sm text-muted-foreground">
              Installez l'app pour un accès rapide et une meilleure expérience.
            </p>
            <div className="text-sm bg-muted p-3 rounded-lg space-y-1">
              <p className="font-medium">Pour installer :</p>
              <ol className="list-decimal list-inside space-y-1 text-muted-foreground">
                <li>Appuyez sur <span className="font-semibold">Partager</span> <Download className="inline h-3 w-3" /></li>
                <li>Sélectionnez <span className="font-semibold">"Sur l'écran d'accueil"</span></li>
                <li>Appuyez sur <span className="font-semibold">Ajouter</span></li>
              </ol>
            </div>
          </div>
        </div>
      </Card>
    );
  }

  // Version Android/Desktop (prompt natif)
  return (
    <Card 
      className="fixed bottom-20 md:bottom-4 left-4 right-4 md:left-auto md:right-4 md:max-w-md p-4 shadow-lg border-2 animate-slide-up"
      style={{ zIndex: Z_INDEX.toast }}
    >
      <button
        onClick={handleDismiss}
        className="absolute top-2 right-2 p-1 hover:bg-muted rounded-full transition-colors"
        aria-label="Fermer"
      >
        <X className="h-4 w-4" />
      </button>

      <div className="flex items-start gap-3 pr-6">
        <div className="bg-primary/10 p-2 rounded-full flex-shrink-0">
          <Download className="h-5 w-5 text-primary" />
        </div>
          <div className="flex-1 space-y-3">
          <div>
            <h3 className="font-semibold">Installer First Tuesday</h3>
            <p className="text-sm text-muted-foreground mt-1">
              Installez l'application pour un accès rapide depuis votre {isMobile ? "écran d'accueil" : "bureau"}.
            </p>
          </div>
          
          <div className="flex gap-2">
            <Button 
              onClick={handleInstallClick} 
              size="sm"
              className="flex-1"
            >
              <Download className="h-4 w-4 mr-2" />
              Installer
            </Button>
            <Button 
              onClick={handleDismiss} 
              variant="outline" 
              size="sm"
            >
              Plus tard
            </Button>
          </div>
        </div>
      </div>
    </Card>
  );
};
